import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PasLayoutSidebarHolderComponent, PasSidebarHandleService } from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';

import { RecourseBookingDto } from '../../../api/tur-rc/models';
import { AGGridExcelService } from '../../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { checkBookingRights } from '../../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { BookingRoutingService } from '../../../services/details-view-service/booking-routing.service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { TableViewService } from '../../../services/reporting/table-view.service';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { BookingStateEnum } from '../../../shared/enums/booking-state-enum';
import { TableViewAdministrationState } from '../../../sub-component/table-view-administration/common/enum/table-view-administration-state.enum';
import { TableViewCustomModel } from '../../../sub-component/table-view-administration/tva-custom/common/model/table-view-custom-model';
import { TvaCustomSelectionComponent } from '../../../sub-component/table-view-administration/tva-custom/component-selection/tva-custom-selection.component';
import {
  NOTIFICATION_UNEXPECTED_ERROR,
  TvaCustomSidebarComponent,
} from '../../../sub-component/table-view-administration/tva-custom/component-sidebar/tva-custom-sidebar.component';
import { TvaCustomService } from '../../../sub-component/table-view-administration/tva-custom/tva-custom.service';
import { TvaSessionService } from '../../../sub-component/table-view-administration/tva-session/tva-session.service';
import { RecourseBookingTabsetComponent } from '../recourse-booking-tabset.component';
import { RecourseBookingButtonCellRenderer } from '../renderer/recourse-booking-button-cell-renderer.component';
import { RecourseTypeCellRenderer } from '../renderer/recourse-type-cell-renderer/recourse-type-cell-renderer';

@Component({
  selector: 'app-recourse-booking',
  templateUrl: './recourse-booking.component.html',
  styleUrls: ['./recourse-booking.component.scss'],
  imports: [
    TvaCustomSelectionComponent,
    AgGridAngular,
    PasLayoutSidebarHolderComponent,
    TranslatePipe,
    NgClass,
    TvaCustomSidebarComponent,
  ],
})
export class RecourseBookingComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly translateService = inject(TranslateService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly userService = inject(UserService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly tableViewService = inject(TableViewService);
  private readonly tvaCustomService = inject(TvaCustomService);
  private readonly tvaSessionService = inject(TvaSessionService);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly recourseBookingTabset = inject(RecourseBookingTabsetComponent, { host: true });

  public gridOptions: GridOptions;
  private gridApi: any;
  public columnDefs: any;
  public defaultColDef: any;
  public frameworkComponents: any;
  public multiSortKey: any;
  public context: any;
  public excelStyles: any;
  public isCreateExcelBookingEnabled = false;
  public tooltipShowDelay = 0;
  public numberOfButtonsVisibleSidebar = 2;
  public bookingsOverviewData: RecourseBookingDto[] = [];
  public listTableViews: TableViewCustomModel[] = [];

  public ngOnInit(): void {
    this.context = {
      componentParent: this,
      statesHiglightedRed: [BookingStateEnum.FAILED, BookingStateEnum.BLOCKED, BookingStateEnum.REJECTED],
    };
    this.multiSortKey = 'ctrl';
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };
    this.gridOptions.rowClassRules = {
      'red-characters': 'ctx.statesHiglightedRed.includes(data.bookingStateId)',
    };
    this.frameworkComponents = {
      recourseBookingButtonCellRenderer: RecourseBookingButtonCellRenderer,
      recourceTypeCellRenderer: RecourseTypeCellRenderer,
    };
    this.defaultColDef = {
      editable: false,
      resizable: true,
      sortable: true,
      floatingFilter: true,
      minWidth: 80,
      unSortIcon: true,
      filter: true,
    };

    this.setupColumnDefs();

    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);
  }

  private setupColumnDefs(): void {
    this.translateService.get('OVERVIEW_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_BELEG_NR'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_BELEG_NR'),
          cellStyle: { textAlign: 'right' },
          field: 'pkBookingId',
          filter: 'agNumberColumnFilter',
          width: 80,
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_LIEFERANT'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_LIEFERANT'),
          field: 'supplierData.name',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_LIEFERANT_NR'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_LIEFERANT_NR'),
          cellStyle: { textAlign: 'right' },
          field: 'supplierData.supplier',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_DEBITOR_NR'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_DEBITOR_NR'),
          field: 'supplierData.debitor',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_BOOKING_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_BOOKING_TYPE'),
          valueGetter: this.agGridService.translateBookingType(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressMiniFilter: true,
          },
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_NETTOWERT'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_NETTOWERT'),
          cellStyle: { textAlign: 'right' },
          valueGetter: this.agGridService.getEurAmountWithBookingTypeCheck(),
          filter: false,
          cellClass: this.agGridFormatterService.cellClassCurrencyType,
          valueFormatter: this.agGridFormatterService.currencyFormatter(this.userService),
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_STATUS'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_STATUS'),
          valueGetter: this.agGridService.getBookingStateName(this.translateService),
          filter: 'agSetColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_RECOURSE_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_RECOURSE_TYPE'),
          valueGetter: this.agGridService.getRecourseType(this.translateService),
          filter: 'agSetColumnFilter',
          menuTabs: ['generalMenuTab', 'filterMenuTab'],
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_ANGELEGT_AM'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_ANGELEGT_AM'),
          ...AG_DATE_COL_CONFIG,
          field: 'createDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          minWidth: 155,
          cellStyle: { textAlign: 'right' },
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_CREATION_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_CREATION_TYPE'),
          filter: 'agSetColumnFilter',
          width: 50,
          valueGetter: this.agGridService.convertCreationType(),
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_ERSTELLER'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_ERSTELLER'),
          field: 'originator',
          filter: 'agSetColumnFilter',
        },
        // the spread operator (...) for Array literals does nothing if its operand is an empty Array
        // so this columnDef is inserted conditionaly if user has RAD_READ rights
        ...(this.userService.getRights().includes(UserRights.RAD_READ)
          ? [
              {
                headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_REVENUE_ORDER_NUMBER'),
                headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_REVENUE_ORDER_NUMBER'),
                field: 'revenueOrderNumber',
                filter: 'agTextColumnFilter',
                hide: true,
              },
            ]
          : []),
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_FAKTURAGRUND'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_FAKTURAGRUND'),
          field: 'invoiceReasonText',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_DUE_DATE'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_DUE_DATE'),
          ...AG_DATE_COL_CONFIG,
          hide: true,
          field: 'dueDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          minWidth: 155,
          cellStyle: { textAlign: 'right' },
          cellClass: 'dateFormat',
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          field: 'value',
          cellRenderer: 'recourseBookingButtonCellRenderer',
          filter: false,
          menuTabs: [],
          unSortIcon: false,
          minWidth: 130,
        },
      ];
    });
  }

  public ngAfterViewInit(): void {
    this.cdRef.detectChanges(); // prevent angular error in dev mode
    if (!this.canUserCreateManualBooking()) {
      this.numberOfButtonsVisibleSidebar = 1;
    }
  }

  public enableSidebar(): void {
    if (this.userHasAnyRight()) {
      // enable and show the sidebar for this component
      this.sidebarHandleService.enableSidebarForView();
    }
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.gridApi = event.api;
    this.gridApi.updateGridOptions({
      headerHeight: 32,
    });

    this.tableViewService
      .getTableViewsFromRegisteredUserByTableViewArea(TableViewAdministrationState.BookingOverview)
      .subscribe({
        next: async resultListTableView => {
          this.listTableViews = resultListTableView;
          this.bookingsOverviewData = await this.recourseBookingStore.getRecourseBookings();
          this.gridApi.updateGridOptions({
            rowData: this.bookingsOverviewData,
          });
          this.isCreateExcelBookingEnabled = this.userHasAnyRight();
          this.tvaSessionService.init(
            TableViewAdministrationState.BookingOverview,
            false,
            this.gridOptions,
            this.tvaCustomService,
            this.gridApi,
          );
          this.tvaCustomService.init(
            TableViewAdministrationState.BookingOverview,
            this.listTableViews,
            false,
            this.gridOptions,
            this.gridApi,
          );
          if (!this.tvaSessionService.isTableViewFromTableViewSessionAvailable) {
            this.tvaCustomService.doSelectedTableViewOnComponent();
          }
        },
        error: err => {
          this.tvaCustomService.tvaCustomSidebarComponent.showNotification(NOTIFICATION_UNEXPECTED_ERROR, null);
          // eslint-disable-next-line no-console
          console.log(err);
        },
      });
  }

  public getContextMenuItems(): [] {
    return [];
  }

  public onBtnCreateManualBooking(): void {
    this.bookingRoutingService.createNewBooking();
  }

  public onBtnCreateExcelBooking(): void {
    try {
      this.createExcelBooking();
    } catch {
      const title: string = this.translateService.instant('EXCEL_EXPORT_ERROR_TITLE');
      const content: string = this.translateService.instant('EXCEL_EXPORT_ERROR_CONTENT');
      this.notificationServiceWrapper.addErrorNotification(title, content);
    }
  }

  public createExcelBooking(): void {
    this.gridApi.exportDataAsExcel(
      this.agGridExcelService.getExcelFrame(
        this.translateService.instant('RECOURSE_BOOKING_TAB'),
        this.translateService.instant('EXCEL_EXPORT_BOOKING_OVERVIEW'),
        this.gridOptions,
      ),
    );
  }

  public ngOnDestroy(): void {
    this.sidebarHandleService.disableSidebarForView();
  }

  public canUserCreateManualBooking(): boolean {
    return checkBookingRights(this.userService.getRights());
  }

  public userHasAnyRight(): boolean {
    return this.userService.getRights().length > 0;
  }

  public isOverviewTabActive(): boolean {
    return this.recourseBookingTabset.isOverviewTabActive();
  }
}
